// Ultimate Card Game (UCG) - Client-side account identity (K-1.6)
//
// Anonymous account-ID + token model: no email, no password. On first
// visit, silently registers an account via api/register.php and stores
// {accountId, secret} in localStorage; every later boot reads it back.
// Failure at any point (offline, Strato unreachable) is never fatal - the
// game simply plays unauthenticated, exactly as it did before this system
// existed. See docs/GO_LIVE_PLAN_2026-09.md section 2.

import { applySnapshot, resetLocalGameState, getLocalSaveVersion } from './cloudSync.js';
import { hasAlphaAccess, grantAlphaAccess } from '../ui/alphaGate.js';

const STORAGE_KEY_ACCOUNT_ID = 'ucg_account_id';
const STORAGE_KEY_SECRET = 'ucg_account_secret';
const STORAGE_KEY_NAME = 'ucg_player_name';
const STORAGE_KEY_MMR = 'ucg_account_mmr';
const STORAGE_KEY_MMR_2V2 = 'ucg_account_mmr_2v2';
const STORAGE_KEY_SELF_LINK_FP = 'ucg_account_link_fp';
const LEGACY_STORAGE_KEY_RECOVERY_CODE = 'ucg_recovery_code';

// Computes a deterministic, non-reversible 64-bit salted fingerprint bound to
// a specific accountId. Allows 0ms offline detection of whether an incoming
// #a=... bookmark link belongs to the already-active local Commander without
// ever storing the recovery code in localStorage (Zero-Knowledge Doctrine).
export function computeSelfLinkFingerprint(accountId, rawCode) {
  const idNum = Number(accountId);
  if (!idNum || !rawCode) return null;
  const normalized = String(rawCode).replace(/[\s-]+/g, '').toUpperCase();
  if (normalized.length !== 12) return null;

  const payload = `ucg-self-fp-v1:${idNum}:${normalized}`;
  let h1 = 0x811c9dc5 >>> 0;
  let h2 = 0x8a1c9dc5 >>> 0;
  for (let round = 0; round < 4; round++) {
    for (let i = 0; i < payload.length; i++) {
      const c = payload.charCodeAt(i);
      h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
      h2 = Math.imul(h2 ^ ((c + round + i) & 0xff), 0x1000193b) >>> 0;
    }
    h1 = (Math.imul(h1 ^ (h2 >>> 13), 0x85ebca6b) ^ (h1 >>> 16)) >>> 0;
    h2 = (Math.imul(h2 ^ (h1 >>> 15), 0xc2b2ae35) ^ (h2 >>> 13)) >>> 0;
  }
  const hex1 = h1.toString(16).padStart(8, '0');
  const hex2 = h2.toString(16).padStart(8, '0');
  return `${idNum}:${hex1}${hex2}`;
}

export function rememberSelfRecoveryCode(accountId, rawCode) {
  try {
    const fp = computeSelfLinkFingerprint(accountId, rawCode);
    if (fp && typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_SELF_LINK_FP, fp);
    }
  } catch {}
}

export function isSelfRecoveryCode(rawCode, accountId = null) {
  try {
    const targetId = accountId ?? cached?.accountId ?? readStoredAuth()?.accountId;
    if (!targetId || typeof localStorage === 'undefined') return false;
    const storedFp = localStorage.getItem(STORAGE_KEY_SELF_LINK_FP);
    if (!storedFp) return false;
    const candidateFp = computeSelfLinkFingerprint(targetId, rawCode);
    return Boolean(candidateFp && storedFp === candidateFp);
  } catch {
    return false;
  }
}

function readStoredAuth() {
  try {
    // Zero-Knowledge Doctrine: strictly purge any legacy plaintext recovery key from localStorage
    localStorage.removeItem(LEGACY_STORAGE_KEY_RECOVERY_CODE);
    const accountId = localStorage.getItem(STORAGE_KEY_ACCOUNT_ID);
    const secret = localStorage.getItem(STORAGE_KEY_SECRET);
    const displayName = localStorage.getItem(STORAGE_KEY_NAME) || null;
    const mmr = localStorage.getItem(STORAGE_KEY_MMR) ? Number(localStorage.getItem(STORAGE_KEY_MMR)) : 1500;
    const mmr_2v2 = localStorage.getItem(STORAGE_KEY_MMR_2V2) ? Number(localStorage.getItem(STORAGE_KEY_MMR_2V2)) : 1500;
    if (accountId && secret) {
      return { accountId: Number(accountId), secret, displayName, mmr, mmr_2v2 };
    }
  } catch {
    // localStorage unavailable (private browsing, disabled) - treat as unset.
  }
  return null;
}

let cached = readStoredAuth(); // Synchronously initialized from localStorage
let ensurePromise = null;
const listeners = new Set();

function notifyListeners() {
  for (const fn of listeners) {
    try { fn(cached); } catch (e) { console.error(e); }
  }
}

export function addIdentityListener(fn) {
  listeners.add(fn);
  if (cached) fn(cached);
  return () => listeners.delete(fn);
}

export function initAuthFromStorage() {
  cached = readStoredAuth();
  notifyListeners();
  return cached;
}

function storeAuth({ accountId, secret, displayName, mmr, mmr_2v2 }) {
  try {
    // Enforce Zero-Knowledge Doctrine: recovery keys are never written to disk/localStorage
    localStorage.removeItem(LEGACY_STORAGE_KEY_RECOVERY_CODE);
    if (accountId !== undefined) localStorage.setItem(STORAGE_KEY_ACCOUNT_ID, String(accountId));
    if (secret !== undefined) localStorage.setItem(STORAGE_KEY_SECRET, secret);
    if (displayName !== undefined) {
      if (displayName) localStorage.setItem(STORAGE_KEY_NAME, displayName);
      else localStorage.removeItem(STORAGE_KEY_NAME);
    }
    if (mmr !== undefined) localStorage.setItem(STORAGE_KEY_MMR, String(mmr));
    if (mmr_2v2 !== undefined) localStorage.setItem(STORAGE_KEY_MMR_2V2, String(mmr_2v2));
  } catch {
    // Storage failed
  }
}

async function postJson(path, body) {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  return { ok: res.ok, data };
}

// Ensures a device identity exists, silently registering a brand-new
// anonymous account on first visit. Safe to call multiple times - only
// ever registers once per page load. Resolves to null on any failure
// (offline, Strato down, malformed response); resolves to
// { accountId, secret, recoveryCode } exactly once, the moment a *new*
// account is created, so the caller can show the code - never again
// afterwards, since the server itself never stores or re-sends it.
export async function ensureIdentity() {
  if (cached) return cached;
  if (ensurePromise) return ensurePromise;

  ensurePromise = (async () => {
    const stored = readStoredAuth();
    if (stored) {
      cached = stored;
      fetchProfile().catch(() => {});
      return cached;
    }

    try {
      const { ok, data } = await postJson('api/register.php', {});
      if (!ok || typeof data?.accountId !== 'number' || typeof data?.secret !== 'string') {
        return null;
      }
      storeAuth(data);
      if (data.recoveryCode) {
        rememberSelfRecoveryCode(data.accountId, data.recoveryCode);
      }
      cached = { accountId: data.accountId, secret: data.secret, displayName: null, mmr: 1500 };
      // Only this in-memory return carries recoveryCode - readStoredAuth()
      // above never will, by design.
      return { ...cached, recoveryCode: data.recoveryCode };
    } catch {
      return null;
    } finally {
      if (!cached) {
        ensurePromise = null;
      }
    }
  })();

  return ensurePromise;
}

// Synchronous best-effort read for hot paths (WSMatchClient attaching
// `auth` to an outgoing message). Returns null until ensureIdentity() has
// resolved at least once this page load - callers that need it guaranteed
// should await ensureIdentity() during boot first.
export function getAuth() {
  return cached;
}

// Lightweight preview of a recovery code without adopting it or fetching the
// account secret / save snapshot. Used to determine whether an incoming #a=...
// link belongs to the already-active local Commander or a different account.
export async function previewRecoveryCode(recoveryCode) {
  try {
    const { ok, data } = await postJson('api/recover.php', { recoveryCode, preview: true });
    if (!ok || typeof data?.accountId !== 'number') {
      return {
        ok: false,
        error: data?.error || 'error',
        message: data?.message || 'Invalid recovery code or account not found.',
      };
    }
    return {
      ok: true,
      accountId: data.accountId,
      displayName: data.displayName || null,
      mmr: typeof data.mmr === 'number' ? data.mmr : 1500,
    };
  } catch {
    return { ok: false, error: 'network_error', message: 'Network connection failed.' };
  }
}

// Exchanges a recovery code for the account it belongs to and adopts it on
// this device, overwriting whatever identity is currently stored - mirrors
// api/recover.php. Returns { ok, accountId } on success.
export async function recoverAccount(recoveryCode) {
  try {
    const { ok, data } = await postJson('api/recover.php', { recoveryCode });
    if (!ok || typeof data?.accountId !== 'number' || typeof data?.secret !== 'string') {
      return {
        ok: false,
        error: data?.error || 'error',
        message: data?.message || 'Invalid recovery code or account not found.',
      };
    }

    const previousAccountId = cached?.accountId ?? readStoredAuth()?.accountId ?? null;
    const isSameAccount = previousAccountId != null && Number(previousAccountId) === Number(data.accountId);

    // Zero-Leakage: Reset local game state before adopting a DIFFERENT identity so
    // prior account unlocks, currency, and custom decks are never inherited or pushed.
    // When refreshing/recovering the SAME account, preserve local progress.
    if (!isSameAccount) {
      resetLocalGameState();
    }

    storeAuth(data);
    rememberSelfRecoveryCode(data.accountId, recoveryCode);
    cached = {
      accountId: data.accountId,
      secret: data.secret,
      displayName: data.displayName || (isSameAccount ? cached?.displayName : null) || null,
      mmr: typeof data.mmr === 'number' ? data.mmr : (isSameAccount ? (cached?.mmr ?? 1500) : 1500),
      isExpired: false,
    };
    notifyListeners();

    // Restoring a verified account grants Alpha access
    grantAlphaAccess();

    // Restore OCC Cloud Save snapshot if returned by server.
    // For the same account, only apply if the server version is newer or local has never synced.
    if (data.saveData) {
      const serverVersion = Number(data.saveVersion || 0);
      const localVersion = getLocalSaveVersion();
      if (!isSameAccount || serverVersion > localVersion || localVersion === 0) {
        applySnapshot(data.saveData, data.saveVersion);
      }
    }

    // Fetch profile for recovered account
    fetchProfile().catch(() => {});
    return { ok: true, accountId: data.accountId, isSameAccount };
  } catch (err) {
    return { ok: false, error: 'network_error', message: 'Network connection failed. Try again.' };
  }
}

export function getDisplayName() {
  if (cached?.displayName) return cached.displayName;
  try {
    return localStorage.getItem(STORAGE_KEY_NAME) || 'Commander';
  } catch {
    return 'Commander';
  }
}

export function getMMR(mode = '1v1') {
  if (mode === '2v2') {
    if (cached?.mmr_2v2 != null) return cached.mmr_2v2;
    try {
      const raw = localStorage.getItem(STORAGE_KEY_MMR_2V2);
      return raw ? Number(raw) : 1500;
    } catch {
      return 1500;
    }
  }
  if (cached?.mmr != null) return cached.mmr;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_MMR);
    return raw ? Number(raw) : 1500;
  } catch {
    return 1500;
  }
}

export async function fetchProfile() {
  if (!cached && !readStoredAuth()) return null;
  const auth = cached || readStoredAuth();
  if (!auth?.accountId || !auth?.secret) return null;

  try {
    const { ok, data } = await postJson('api/profile.php', {
      accountId: auth.accountId,
      secret: auth.secret,
    });
    if (ok && data) {
      cached = {
        ...auth,
        displayName: data.displayName || null,
        mmr: Number(data.mmr) || 1500,
        wins: Number(data.wins) || 0,
        losses: Number(data.losses) || 0,
        winRate: Number(data.winRate) || 0,
        mmr_2v2: Number(data.mmr_2v2) || 1500,
        wins_2v2: Number(data.wins_2v2) || 0,
        losses_2v2: Number(data.losses_2v2) || 0,
        winRate_2v2: Number(data.winRate_2v2) || 0,
        isExpired: false
      };
      storeAuth(cached);
      notifyListeners();
      return cached;
    } else if (!ok && data?.error === 'unauthorized') {
      await handleUnauthorizedIdentity();
    }
  } catch {
    // Network failure
  }
  return cached;
}

let hasReauthedThisSession = false;

export async function handleUnauthorizedIdentity() {
  if (cached) {
    cached = { ...cached, isExpired: true };
  }
  try {
    localStorage.removeItem(STORAGE_KEY_ACCOUNT_ID);
    localStorage.removeItem(STORAGE_KEY_SECRET);
  } catch {}
  notifyListeners();

  if (!hasReauthedThisSession) {
    hasReauthedThisSession = true;
    cached = null;
    ensurePromise = null;
    try {
      const fresh = await ensureIdentity();
      if (fresh) {
        notifyListeners();
        return fresh;
      }
    } catch {}
  }
  return null;
}

export function isSessionExpired() {
  return !!cached?.isExpired;
}

export async function setDisplayName(name) {
  if (!hasAlphaAccess()) {
    return { ok: false, message: 'Closed Alpha clearance required.' };
  }

  let auth = cached || readStoredAuth();
  let newRecoveryCode = null;
  if (!auth?.accountId || !auth?.secret) {
    const registered = await ensureIdentity();
    auth = registered;
    if (registered?.recoveryCode) {
      newRecoveryCode = registered.recoveryCode;
    }
  }
  if (!auth?.accountId || !auth?.secret) {
    return { ok: false, message: 'No active Commander account.' };
  }

  try {
    const res = await fetch('api/set_name.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        accountId: auth.accountId,
        secret: auth.secret,
        name,
      }),
    });
    const data = await res.json().catch(() => null);
    if (res.ok && data?.ok) {
      cached = {
        accountId: auth.accountId,
        secret: auth.secret,
        displayName: data.displayName,
        mmr: auth.mmr ?? 1500,
        mmr_2v2: auth.mmr_2v2 ?? 1500,
      };
      storeAuth(cached);
      notifyListeners();
      return {
        ok: true,
        displayName: data.displayName,
        recoveryCode: newRecoveryCode || null,
        isNewAccount: !!newRecoveryCode
      };
    }
    return {
      ok: false,
      code: data?.error || 'error',
      message: data?.message || 'Could not update Call-Sign.',
    };
  } catch (err) {
    return { ok: false, message: 'Network connection failed.' };
  }
}

